import React, { type PropsWithChildren, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classnames from 'classnames';
import { useOnClickOutside } from 'usehooks-ts';

import styles from './CustomDropdown.module.css';

const CustomDropdown: React.FC<
  PropsWithChildren<{
    dropdownRef: React.RefObject<HTMLDivElement | null>;
    isUkStyle?: boolean;
    label: string;
    variant?: 'secondary' | 'tertiary';
  }>
> = ({ children, dropdownRef, isUkStyle, label = 'More', variant }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Registered on mount and guarded by refs, so it is immune to the Astro
  // island-vs-document event-dispatch quirk that made a listener added during
  // the opening click close the dropdown within that same click. Mirrors
  // NavExpandable in HeaderNavItem.tsx.
  useOnClickOutside(ref as React.RefObject<HTMLElement>, event => {
    const target = event.target as Node | null;
    if (dropdownRef.current && target && dropdownRef.current.contains(target)) {
      return;
    }
    setIsOpen(false);
  });

  if (!children) {
    return null;
  }

  // The secondary redesign panel is gated behind isUkStyle (RENDER_UK_HEADER_STYLE), not just the
  // variant, so a secondary SPLIT_EXPANDABLE served with the flag off falls back to the plain
  // .dropdownContainer instead of leaking the UK restyle into the legacy header. When it applies,
  // the panel's own max-height (.secondaryDropdownContainer) is used instead of the inline default.
  const isSecondaryUk = variant === 'secondary' && Boolean(isUkStyle);

  const renderDropdownLinks = () => {
    if (isOpen) {
      return (
        dropdownRef.current &&
        createPortal(
          <div
            className={classnames(styles.dropdownContainer, isSecondaryUk && styles.secondaryDropdownContainer)}
            data-testid="custom-dropdown-modal"
            style={isSecondaryUk ? undefined : { maxHeight: `calc(100vh - 154px)` }}
          >
            {children}
          </div>,
          dropdownRef.current,
        )
      );
    }

    return (
      <div className={styles.hidden} data-testid="custom-dropdown-ssr-links">
        {children}
      </div>
    );
  };

  return (
    <>
      <div
        className={styles.dropdownLabel}
        data-testid="custom-dropdown-button"
        onClick={() => setIsOpen(isOpen => !isOpen)}
        ref={ref}
      >
        {label}
        <span
          className={classnames(
            styles.arrow,
            isSecondaryUk && styles.arrowUk,
            'iconarrow_rightWhite',
            'iconarrow_rightWhitesize',
            isOpen ? styles.arrowOpen : styles.arrowClose,
          )}
        />
      </div>
      {renderDropdownLinks()}
    </>
  );
};

export default CustomDropdown;
